// Patient endpoints (docs/plans/phase1-registration.md §3.3). Search terms and
// demographics are PHI: they only ever travel in POST or PATCH bodies, never in a path
// or query string. Paths carry internal UUIDs only, never patient numbers.
import {
  duplicateCheckResponseSchema,
  type enteredInErrorRequestSchema,
  pageSchema,
  patientBannerSchema,
  patientConsentHistorySchema,
  patientConsentSchema,
  patientContactSchema,
  patientDetailSchema,
  patientHistoryEntrySchema,
  patientIdentifierSchema,
  patientRelationshipSchema,
  patientSearchResponseSchema,
  registrySyncStatusSchema,
  visitSummarySchema,
  type addContactRequestSchema,
  type addIdentifierRequestSchema,
  type addRelationshipRequestSchema,
  type duplicateCheckRequestSchema,
  type endRelationshipRequestSchema,
  type recordConsentRequestSchema,
  type registerPatientRequestSchema,
  type updatePatientRequestSchema,
} from '@wodi/shared';
import { queryOptions } from '@tanstack/react-query';
import type { z } from 'zod';

import { api } from './client.ts';

export const patientKeys = {
  all: ['patients'] as const,
  detail: (id: string) => ['patients', id, 'detail'] as const,
  banner: (id: string) => ['patients', id, 'banner'] as const,
  history: (id: string) => ['patients', id, 'history'] as const,
  consents: (id: string) => ['patients', id, 'consents'] as const,
  visits: (id: string) => ['patients', id, 'visits'] as const,
};

export function searchPatients(query: string) {
  return api('POST', '/patients/search', {
    body: { query, limit: 20 },
    schema: patientSearchResponseSchema,
  });
}

export const patientDetailQuery = (id: string) =>
  queryOptions({
    queryKey: patientKeys.detail(id),
    queryFn: () => api('GET', `/patients/${id}`, { schema: patientDetailSchema }),
  });

export const patientBannerQuery = (id: string) =>
  queryOptions({
    queryKey: patientKeys.banner(id),
    queryFn: () => api('GET', `/patients/${id}/banner`, { schema: patientBannerSchema }),
  });

export const historyPage = pageSchema(patientHistoryEntrySchema);
export const visitPage = pageSchema(visitSummarySchema);

export function patientHistory(id: string, cursor: string | undefined) {
  const query = cursor === undefined ? '?limit=50' : `?limit=50&cursor=${cursor}`;
  return api('GET', `/patients/${id}/history${query}`, { schema: historyPage });
}

/** Every data-sharing consent decision, newest first. */
export const patientConsentsQuery = (id: string) =>
  queryOptions({
    queryKey: patientKeys.consents(id),
    queryFn: () => api('GET', `/patients/${id}/consents`, { schema: patientConsentHistorySchema }),
  });

export function patientVisits(id: string, cursor: string | undefined) {
  const query = cursor === undefined ? '?limit=20' : `?limit=20&cursor=${cursor}`;
  return api('GET', `/patients/${id}/visits${query}`, { schema: visitPage });
}

export function duplicateCheck(body: z.input<typeof duplicateCheckRequestSchema>) {
  return api('POST', '/patients/duplicate-check', {
    body,
    schema: duplicateCheckResponseSchema,
  });
}

/** The request as the client builds it; the server applies the schema's defaults. */
export type RegisterPatientInput = z.input<typeof registerPatientRequestSchema>;

export function registerPatient(body: RegisterPatientInput) {
  return api('POST', '/patients', { body, schema: patientDetailSchema });
}

export function updatePatient(id: string, body: z.input<typeof updatePatientRequestSchema>) {
  return api('PATCH', `/patients/${id}`, { body, schema: patientDetailSchema });
}

export function markPatientEnteredInError(
  id: string,
  body: z.input<typeof enteredInErrorRequestSchema>,
) {
  return api('POST', `/patients/${id}/entered-in-error`, { body, schema: patientDetailSchema });
}

export function addIdentifier(id: string, body: z.input<typeof addIdentifierRequestSchema>) {
  return api('POST', `/patients/${id}/identifiers`, { body, schema: patientIdentifierSchema });
}

export function identifierEnteredInError(id: string, identifierId: string, reason: string) {
  return api('POST', `/patients/${id}/identifiers/${identifierId}/entered-in-error`, {
    body: { reason },
    schema: patientIdentifierSchema,
  });
}

export function addContact(id: string, body: z.input<typeof addContactRequestSchema>) {
  return api('POST', `/patients/${id}/contacts`, { body, schema: patientContactSchema });
}

export function contactEnteredInError(id: string, contactId: string, reason: string) {
  return api('POST', `/patients/${id}/contacts/${contactId}/entered-in-error`, {
    body: { reason },
    schema: patientContactSchema,
  });
}

export function addRelationship(id: string, body: z.input<typeof addRelationshipRequestSchema>) {
  return api('POST', `/patients/${id}/relationships`, {
    body,
    schema: patientRelationshipSchema,
  });
}

export function endRelationship(
  id: string,
  relationshipId: string,
  body: z.input<typeof endRelationshipRequestSchema>,
) {
  return api('POST', `/patients/${id}/relationships/${relationshipId}/end`, {
    body,
    schema: patientRelationshipSchema,
  });
}

export function recordConsent(id: string, body: z.input<typeof recordConsentRequestSchema>) {
  return api('POST', `/patients/${id}/consents`, { body, schema: patientConsentSchema });
}

export function retryRegistrySync(id: string) {
  return api('POST', `/patients/${id}/registry-sync/retry`, {
    schema: registrySyncStatusSchema,
  });
}
